import { useEffect, useMemo, useRef, useState } from "react";
import { MenuItem, Select, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import colors from "../../Constants/colors";
import {
  UsagePreset,
  buildPeriod,
  dateInputToFrom,
  dateInputToTo,
  toIsoDateOnly,
} from "../../Helpers/usageWindow";

interface Props {
  defaultPreset?: UsagePreset;
  onChange: (range: { from?: string; to?: string }) => void;
  size?: "small" | "medium";
}

const PRESETS: UsagePreset[] = [
  "thisMonth",
  "lastDay",
  "last3Days",
  "last7Days",
  "lastMonth",
  "allTime",
  "custom",
];

const UsagePeriodSelector = ({
  defaultPreset = "thisMonth",
  onChange,
  size = "small",
}: Props) => {
  const { t } = useTranslation("usage");

  const [preset, setPreset] = useState<UsagePreset>(defaultPreset);

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const [customFrom, setCustomFrom] = useState<string>(
    toIsoDateOnly(monthStart),
  );
  const [customTo, setCustomTo] = useState<string>(toIsoDateOnly(now));

  const range = useMemo(() => {
    if (preset === "custom") {
      return { from: dateInputToFrom(customFrom), to: dateInputToTo(customTo) };
    }
    return buildPeriod(preset);
  }, [preset, customFrom, customTo]);

  // Emit whenever the resolved range changes. A ref keeps the latest callback
  // without making it a dependency (which would re-fire on every parent render).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current(range);
  }, [range.from, range.to]);

  const controlSx = {
    color: colors.black,
    backgroundColor: colors.white,
    fontSize: "0.8rem",
    minWidth: 160,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.gray300 },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.gray400 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accent,
    },
    "& .MuiSvgIcon-root": { color: colors.gray500 },
  };

  const dateFieldSx = {
    "& .MuiInputBase-input": { color: colors.black },
    "& .MuiInputLabel-root": { color: colors.gray600 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.gray300 },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.gray400 },
    "& input::-webkit-calendar-picker-indicator": { filter: "invert(0.35)" },
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <Select
        size={size}
        value={preset}
        onChange={(e) => setPreset(e.target.value as UsagePreset)}
        sx={controlSx}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: colors.white,
              border: `1px solid ${colors.gray200}`,
              "& .MuiMenuItem-root": {
                color: colors.black,
                fontSize: "0.8rem",
              },
              "& .MuiMenuItem-root:hover": {
                backgroundColor: `${colors.accent}0A`,
              },
              "& .MuiMenuItem-root.Mui-selected": {
                backgroundColor: `${colors.accent}14`,
              },
              "& .MuiMenuItem-root.Mui-selected:hover": {
                backgroundColor: `${colors.accent}22`,
              },
            },
          },
        }}
      >
        {PRESETS.map((p) => (
          <MenuItem key={p} value={p} sx={{ fontSize: "0.8rem" }}>
            {t(p)}
          </MenuItem>
        ))}
      </Select>

      {preset === "custom" && (
        <Stack direction="row" spacing={1.5}>
          <TextField
            type="date"
            size={size}
            label={t("from")}
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={dateFieldSx}
          />
          <TextField
            type="date"
            size={size}
            label={t("to")}
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={dateFieldSx}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default UsagePeriodSelector;
